/**
 * A Stimulus controller that copies the inner text of a target element to the clipboard.
 * @module CopyContentController
 *
 * This controller can be used to copy the inner text of target element
 * with a provided regex string used to filter the content that gets copied.
 *
 * The element that had its contents copied will flash to indicate that the copy was successful:
 * the CSS class "flash" will be added to the element for a short period of time.
 *
 * To hide the copy button until it's parent element is hovered:
 *   1. Add "hover-parent" class to the parent element.
 *   2. Add "hover-element" class to the copy button/icon/link.
 *
 * @example
 *  HTML that displays an SSN with the copy to clipboard icon, and hides the icon until hovered,
 *  and removes the dashes from the SSN before copying it to the clipboard:
 *
 *  <div class="hover-parent" data-controller="clipboard" data-clipboard-regex-value="-">
 *    <span data-clipboard-target="source">123-45-6789</span>
 *    <a href="#" data-action="clipboard#copy" className="hover-element"><i className="fa fa-copy"/></a>
 *  </div>
 */
import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['source'];

  static values = { regex: String };

  copy(event) {
    event.preventDefault();

    const element = this.sourceTarget;
    let content = element.innerText;
    if (this.regexValue) {
      const regex = new RegExp(this.regexValue, 'g');
      content = content.replace(regex, '');
    }

    navigator.clipboard.writeText(content);

    element.classList.add('flash');
    element.addEventListener('animationend', () => element.classList.remove('flash'));
  }
}
